/* eslint-disable react/jsx-props-no-spreading */
import React, { forwardRef, useContext, useEffect } from 'react';
import { TransitionProps } from '@mui/material/transitions';
import {
  Button,
  Dialog, DialogContent, DialogTitle, Grid, IconButton, Zoom,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { yupResolver } from '@hookform/resolvers/yup';
import { ObjectSchema } from 'yup';
import { TypesDialogBox } from '../../../../utils/interface';
import { LocalizationContext } from '../../../../Common/Localization/LocalizationProvider';
import { AddMarkupsData } from '../utils/TypesMarkupsList';
import ComponentTextInput from '../../../../Common/ComponentTextinput/ComponentTextInput';
import { MarkupFormValue } from '../utils/ConstantsMarkup';
import { addMarkupsStart, resetState } from '../Redux/sliceMarkups';
import { RootState } from '../../../../redux/rootState';
import { markupSchema } from '../utils/validation';
import CustomCheckbox from '../../../../Common/ComponentCustomCheckBox';
import { markupPercentageRegex } from '../../../../utils/regex';

const Transition = forwardRef((
  props: TransitionProps & {
    children: React.ReactElement;
  },
  ref: React.Ref<unknown>,
) => <Zoom ref={ref} {...props} />);
function ComponentAddMarkupsDialog({ show, setOpenFrom, handleData }: TypesDialogBox): JSX.Element {
  const { translations } = useContext(LocalizationContext);
  const { isSuccess } = useSelector((state: RootState) => state.SystemMarkupReducer);

  const dispatch = useDispatch();

  const {
    formState: { errors }, control, handleSubmit, setValue, reset,
  } = useForm<AddMarkupsData>({
    resolver: yupResolver(markupSchema as ObjectSchema<AddMarkupsData>),
  });

  const resetValues = () => {
    setValue(MarkupFormValue.DESCRIPTION, '');
    setValue(MarkupFormValue.PERCENTAGE, 0);
    setValue(MarkupFormValue.ALLOW, false);
  };

  const onSubmit = (data: AddMarkupsData) => {
    const bodyData = {
      Description: data.Description,
      Percentage: data.Percentage,
      IsAllowMarkUp: data.IsAllowMarkUp || false,
    };
    dispatch(addMarkupsStart(bodyData));
    setOpenFrom();
  };

  useEffect(() => {
    if (isSuccess) {
      if (handleData) {
        handleData();
      }
      resetValues();
      dispatch(resetState());
    }
  }, [isSuccess]);

  const handleClose = () => {
    reset();
    setOpenFrom();
    resetValues();
  };

  return (
    <div>
      <Dialog
        maxWidth="sm"
        PaperProps={{
          className: 'paperStyle',
        }}
        open={show}
        transitionDuration={500}
        TransitionComponent={Transition}
        keepMounted
        onClose={handleClose}
      >
        <DialogTitle className="modalHeaderTitle">
          {translations.addMarkup}
          <IconButton
            aria-label="close"
            onClick={handleClose}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent style={{ padding: '16px 24px' }}>
          <form onSubmit={handleSubmit(onSubmit)} noValidate>
            <Grid container spacing={2}>
              <Grid item md={12} sm={12} xs={12}>
                <div className="div_label_text">
                  <label className="label_Style_job">{translations.description}</label>
                  <ComponentTextInput
                    control={control}
                    className="nonCapitalizeField"
                    name={MarkupFormValue.DESCRIPTION}
                    size="small"
                    id={MarkupFormValue.DESCRIPTION}
                    errors={errors}
                  />
                </div>
              </Grid>
              <Grid item md={6} sm={6} xs={12}>
                <div className="div_label_text">
                  <label className="label_Style_job">{translations.percentage}</label>
                  <div className="percentage_div">
                    <ComponentTextInput
                      type="number"
                      control={control}
                      regEx={markupPercentageRegex}
                      name={MarkupFormValue.PERCENTAGE}
                      size="small"
                      id={MarkupFormValue.PERCENTAGE}
                      errors={errors}
                      className="width-100"
                    />
                    <div className="percent">%</div>
                  </div>
                </div>
              </Grid>
              <Grid item md={6} sm={6} xs={12}>
                <div style={{
                  display: 'flex', justifyContent: 'end', marginRight: '-15px',
                }}
                >
                  <CustomCheckbox control={control} name={MarkupFormValue.ALLOW} label="Allow min to be marked up" />
                </div>
              </Grid>
              <Grid item md={12} sm={12} xs={12}>
                <div className="save_and_next_div">
                  <div>
                    <Button className="button_cancel" onClick={handleClose}>{translations.cancel}</Button>
                  </div>
                  <div className="button_margin_left">
                    <Button type="submit" className="button_save_and_next">{translations.save}</Button>
                  </div>
                </div>
              </Grid>
            </Grid>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default ComponentAddMarkupsDialog;
